import { useEffect, useMemo, useRef } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import { useAudio } from '../audio/AudioProvider'
import { TheoryPlayer } from '../audio/TheoryPlayer'
import { CredibilityMeter } from '../components/CredibilityMeter'
import { Icon } from '../components/Icon'
import { ErrorState, Loading } from '../components/States'
import { Transcript } from '../components/Transcript'
import { api } from '../lib/api'
import { formatDate, formatLength, plural } from '../lib/format'
import { renderMarkdown } from '../lib/markdown'
import { useAsync } from '../lib/useAsync'
import { useShare } from '../lib/useShare'

export function TheoryPage() {
  const { slug = '' } = useParams()
  const request = useAsync(() => api.theory(slug), [slug])
  const { share, copied } = useShare()
  const [params] = useSearchParams()
  const audio = useAudio()

  const theory = request.data?.theory
  const neighbors = request.data?.neighbors

  const overviewHtml = useMemo(() => (theory ? renderMarkdown(theory.overview) : ''), [theory])

  /**
   * A shared link can point at a moment: /theory/slug?t=247. Fires once per
   * theory — re-running it on every render would drag playback back to the
   * link's timestamp every time the audio state ticked.
   */
  const jumpedTo = useRef<string | null>(null)
  useEffect(() => {
    if (!theory?.audio || jumpedTo.current === theory.slug) return
    const at = Number(params.get('t'))
    if (!Number.isFinite(at) || at <= 0) return
    jumpedTo.current = theory.slug
    audio.play(theory, [], at)
  }, [theory, params, audio])

  if (request.loading) return <div className="page section"><Loading label="Opening the file…" /></div>

  if (request.error || !theory) {
    return (
      <div className="page section">
        <ErrorState message={request.error ?? 'That theory does not exist.'} />
        <p style={{ marginTop: 'var(--space-4)' }}>
          <Link to="/theories" className="btn btn-ghost">
            <Icon name="arrowLeft" size={16} />
            Back to the archive
          </Link>
        </p>
      </div>
    )
  }

  return (
    <article className="theory">
      <div className="page">
        <Link to="/theories" className="back-link">
          <Icon name="arrowLeft" size={16} />
          The archive
        </Link>

        <header className="theory-head">
          <div className="theory-head-meta">
            {/* When he said it beats when it got posted, so lead with that. */}
            <time
              className="eyebrow"
              dateTime={(theory.recordedAt ?? theory.publishedAt) ?? undefined}
            >
              {theory.recordedAt
                ? `Recorded ${formatDate(theory.recordedAt)}`
                : formatDate(theory.publishedAt)}
            </time>
            {theory.audio && (
              <span className="badge badge-audio">
                <Icon name="headphones" size={12} />
                {formatLength(theory.audio.seconds) ?? 'Audio'}
              </span>
            )}
          </div>

          <h1 className="theory-title">{theory.title}</h1>
          {theory.description && <p className="lede theory-description">{theory.description}</p>}

          <div className="theory-head-foot">
            <CredibilityMeter score={theory.credibility} />
            <div className="theory-head-actions">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => void share(window.location.href, theory.title)}
              >
                <Icon name={copied ? 'check' : 'link'} size={15} />
                {copied ? 'Link copied' : 'Share'}
              </button>
            </div>
          </div>
        </header>

        {theory.coverImage && (
          <figure className="theory-cover">
            <img src={theory.coverImage} alt="" loading="lazy" />
          </figure>
        )}

        {theory.audio && <TheoryPlayer theory={theory} />}

        {theory.pullQuote && (
          <figure className="pull-quote">
            <Icon name="quote" size={22} />
            <blockquote>{theory.pullQuote}</blockquote>
            <figcaption className="mono">— Max, verbatim</figcaption>
          </figure>
        )}

        {/* Only render the write-up when there is one. Plenty of theories are
            just the recording, and an empty prose block left a stray gap. */}
        {overviewHtml.trim() && (
          <div className="prose" dangerouslySetInnerHTML={{ __html: overviewHtml }} />
        )}

        {/* Previewed rather than folded away. A verbatim transcript runs many
            times longer than the write-up above it, so it stays capped until
            asked for — but a few lines showing beats a closed grey bar, and the
            playing line tracks the recording once it is open. */}
        <Transcript theory={theory} />

        <footer className="theory-foot">
          {theory.tags.length > 0 && (
            <ul className="cluster" aria-label="Tags">
              {theory.tags.map((tag) => (
                <li key={tag}>
                  <Link to={`/theories?tag=${encodeURIComponent(tag)}`} className="chip">
                    {tag}
                  </Link>
                </li>
              ))}
            </ul>
          )}

          <p className="mono theory-stats">
            <span>
              <Icon name="eye" size={14} /> {plural(theory.views, 'view')}
            </span>
            {theory.audio && (
              <span>
                <Icon name="headphones" size={14} /> {plural(theory.listens, 'listen')}
              </span>
            )}
          </p>
        </footer>

        {(neighbors?.previous || neighbors?.next) && (
          <nav className="theory-nav" aria-label="More theories">
            {neighbors.previous ? (
              <Link to={`/theory/${neighbors.previous.slug}`} className="theory-nav-link">
                <span className="eyebrow">
                  <Icon name="arrowLeft" size={13} /> Previous
                </span>
                <span className="theory-nav-title">{neighbors.previous.title}</span>
              </Link>
            ) : (
              <span />
            )}
            {neighbors.next && (
              <Link to={`/theory/${neighbors.next.slug}`} className="theory-nav-link is-next">
                <span className="eyebrow">
                  Next <Icon name="arrowRight" size={13} />
                </span>
                <span className="theory-nav-title">{neighbors.next.title}</span>
              </Link>
            )}
          </nav>
        )}
      </div>
    </article>
  )
}
